import { Avatar, Clickable, Col, Dropdown, Icon, Row, Spinner2, Text } from 'components';
import { useInfiniteScrollObserver } from 'core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Header, ParticipationSummary, SearchUsers } from 'screens/Admin/components';
import { getTiriItems, getTiriSummary } from 'services';
import { List, TiriPanel } from './components';

const fmtNumber = (n) => (n || 0).toLocaleString();

// Start paging before the list actually bottoms out, so the request and the row append happen
// while there is still content to scroll through. Sized the same way as the Fancams list: the
// server returns 50 rows at 53px each (~2650px), and this must stay well under one page or the
// newly appended rows land inside the margin and chain pages nobody scrolled towards.
const PAGE_AHEAD_PX = 1500;

// `opt` is the API token the list actually filters by and `key` is where its label lives;
// `null` is the "All" entry, which leads every dropdown and is its default — the list opens
// showing everything rather than pre-filtered to a queue. Picking a status implies
// module=tagit: a Rate It-only video has nothing to label, so it must never show up as pending.
//
// Only the tokens are module-level. The labels are resolved inside the component (see
// `useOptions` below), because an array built at module scope would freeze every dropdown at
// whatever language happened to be active when this file was first imported.
const STATUS_OPTS = [
  { opt: null, key: 'admin.tiri.filters.all' },
  { opt: 'PENDING', key: 'admin.tiri.status.pending' },
  { opt: 'LABELED', key: 'admin.tiri.status.labeled' }
];

const MODULE_OPTS = [
  { opt: null, key: 'admin.tiri.filters.all' },
  { opt: 'tagit', key: 'admin.tiri.modules.tagit' },
  { opt: 'rateit', key: 'admin.tiri.modules.rateit' }
];

const SOURCE_OPTS = [
  { opt: null, key: 'admin.tiri.filters.all' },
  { opt: 'fancam', key: 'admin.tiri.sources.fancam' },
  { opt: 'direct', key: 'admin.tiri.sources.direct' }
];

/**
 * The TIRI admin menu — one list of every TIRI item, whether it came from a FanCam Challenge or
 * a direct Team Channel upload, with the ground-truth labeling queue as its default view.
 *
 * This exists because TIRI content no longer has to be a FanCam: a direct upload has no fancam
 * at all, so it can't be reached from the Fancams menu. The pending filter here is also the
 * thing the Fancams menu can never offer — that list has no labeled/unlabeled filter, so
 * admins had to hunt for red badges page by page.
 */
const Tiri = () => {
  const { t } = useTranslation();

  // These hold the API TOKEN (or null for "All"), not the labelled option object. Keeping them
  // primitive is what lets the refetch effects below key on the filters without a re-label
  // re-running them, and what stops a language change from resetting the admin's selection.
  const [statusFilter, setStatusFilter] = useState(null),
    [moduleFilter, setModuleFilter] = useState(null),
    [sourceFilter, setSourceFilter] = useState(null),
    [teamFilter, setTeamFilter] = useState(null),
    [dataId, setDataId] = useState(null),
    [loading, setLoading] = useState(true),
    // Distinct from loading: loading blanks the table for the first page and for filter
    // changes, while paging keeps the rows on screen and shows a spinner underneath them.
    [paging, setPaging] = useState(false),
    [summary, setSummary] = useState(null),
    // Starts true so the first paint is the loader, not a momentary row of zeros.
    [summaryLoading, setSummaryLoading] = useState(true),
    items = useSelector((state) => state.admin?.tiriItems),
    fetching = useRef(false),
    // Only ever set when the server returns an empty page.
    endReached = useRef(false),
    // Row count the auto-fill effect below last acted on, so it only reacts to the list
    // actually growing and cannot spin on a page that dedupes down to nothing new.
    lastFilled = useRef(0),
    skip = useRef(0),
    dispatch = useDispatch(),
    observerTarget = useRef(null),
    // The element that actually scrolls — the Col below sets `overflow: scroll` via `scrollY`,
    // so the page itself never scrolls this list. State rather than a ref because both the
    // observer options and the virtualizer have to rebuild once the node exists, and a ref
    // mutating to non-null does not re-render.
    [scrollEl, setScrollEl] = useState(null);

  // Labels resolved per render of the screen rather than once at import, so switching language
  // re-labels the dropdowns in place while the selected tokens — and the list under them —
  // stay exactly where they were.
  const [statusOptions, moduleOptions, sourceOptions] = useMemo(
    () =>
      [STATUS_OPTS, MODULE_OPTS, SOURCE_OPTS].map((defs) =>
        defs.map((d) => ({ ...d, value: t(d.key) }))
      ),
    [t]
  );

  // Dropdown renders `selected.value`, so the token has to be mapped back to its labelled
  // option. `All` is `opt: null`, which is the first entry and the fallback either way.
  const selected = useMemo(
    () => ({
      status: statusOptions.find((o) => o.opt === statusFilter) || statusOptions[0],
      module: moduleOptions.find((o) => o.opt === moduleFilter) || moduleOptions[0],
      source: sourceOptions.find((o) => o.opt === sourceFilter) || sourceOptions[0]
    }),
    [statusOptions, moduleOptions, sourceOptions, statusFilter, moduleFilter, sourceFilter]
  );

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    const res = await getTiriSummary(teamFilter?._id);
    // This endpoint answers through ApiResponse.success, so the counts arrive under `.data`.
    setSummary(res?.data || null);
    setSummaryLoading(false);
  }, [teamFilter]);

  // Memoized on the filters alone so it keeps a stable identity across the renders paging and
  // the summary fetch cause — useInfiniteScrollObserver keys its effect on this callback, so an
  // unstable identity would tear down and rebuild the observer every render.
  const fetchItems = useCallback(
    async (isFilterChange) => {
      if (isFilterChange) {
        dispatch({ type: 'ADMIN_SET_TIRI', payload: [] });
        setLoading(true);
        skip.current = 0;
        endReached.current = false;
      }

      if (fetching.current || endReached.current) return;

      fetching.current = true;
      if (!isFilterChange) setPaging(true);

      try {
        const res = await getTiriItems({
          skip: skip.current,
          ...(statusFilter
            ? // Only Tag It items have a label state at all, so filtering by it implies the
              // module — otherwise Rate It-only videos would fill the labeling queue.
              { categoryStatus: statusFilter, module: 'tagit' }
            : moduleFilter
              ? { module: moduleFilter }
              : {}),
          ...(sourceFilter ? { source: sourceFilter } : {}),
          ...(teamFilter ? { teamId: teamFilter._id } : {})
        });

        setLoading(false);

        const rows = res?.data;

        if (!rows || rows.length === 0) {
          endReached.current = true;
          return;
        }

        skip.current += rows.length;

        dispatch({ type: 'ADMIN_ADD_TIRI', payload: rows });
      } finally {
        // Released only once the appended rows are in flight to the store, so the intersection
        // that arrives next cannot fetch the same page twice. In a finally so a failed request
        // cannot wedge paging shut.
        fetching.current = false;
        if (!isFilterChange) setPaging(false);
      }
    },
    [dispatch, statusFilter, moduleFilter, sourceFilter, teamFilter]
  );

  // Required by useInfiniteScrollObserver, but the auto-fill effect below reads the sentinel
  // position directly instead of trusting a flag the observer sets asynchronously.
  const bottomTrigger = useCallback(() => {}, []);

  // `root` is the scroll container, not the default viewport. This list scrolls inside an
  // overflow element, and rootMargin only ever expands the observer's own root — it does
  // nothing about the clip an intermediate scrolling ancestor imposes. Left on the viewport the
  // sentinel had to physically enter the container's visible box before it could intersect at
  // all, so the page-ahead margin bought nothing; and because the container is 100vh tall
  // starting 54px down, its bottom edge — where the sentinel comes to rest — sits below the
  // viewport entirely, where a zero-height target can never reach `threshold: 1`. Rooted here
  // the margin expands the container's own box and paging fires PAGE_AHEAD_PX early as intended.
  const pageAhead = useMemo(
    () => ({ root: scrollEl, rootMargin: PAGE_AHEAD_PX + 'px 0px' }),
    [scrollEl]
  );

  useInfiniteScrollObserver(observerTarget, fetchItems, bottomTrigger, pageAhead);

  useEffect(() => {
    fetchItems(true).then();
    fetchSummary().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetching.current || loading) return;
    setDataId(null);
    fetchItems(true).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, moduleFilter, sourceFilter, teamFilter]);

  // The header counts are themselves the split by label state, so only the team filter narrows
  // them — refetching on the status filter would zero out the state filtered away.
  useEffect(() => {
    if (loading) return;
    fetchSummary().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilter]);

  // A page short enough to leave the sentinel still on screen produces no new intersection
  // event, so keep pulling until it is pushed out of view or the list runs dry.
  useEffect(() => {
    const count = items?.length || 0;

    if (count === 0) {
      lastFilled.current = 0;
      return;
    }

    if (count <= lastFilled.current) return;
    if (endReached.current || fetching.current) return;

    // Measured rather than flag-based: the observer reports asynchronously, so on the commit
    // that appended these rows its flag can still say the sentinel is in view when the new rows
    // have already pushed it past the margin.
    const sentinel = observerTarget.current;
    if (!sentinel) return;
    // Against the scroll container's bottom edge rather than the window's: the container is the
    // thing that scrolls, and its bottom is not the viewport's.
    const visibleBottom = scrollEl ? scrollEl.getBoundingClientRect().bottom : window.innerHeight;
    if (sentinel.getBoundingClientRect().top > visibleBottom + PAGE_AHEAD_PX) return;

    lastFilled.current = count;
    fetchItems().then();
  }, [items, fetchItems, scrollEl]);

  const handleRowClick = useCallback((id) => setDataId(id), []);
  const closePanel = useCallback(() => setDataId(null), []);

  const summaryItems = useMemo(
    () => [
      // Deliberately no "Total": `modules` is a set, so a photo is counted in BOTH numbers and
      // they overlap. Shown next to a total they read as broken arithmetic (2 total, 1 + 2).
      // Each number answers "how much content does this module have", which stands on its own.
      { label: t('admin.tiri.modules.tagit'), value: fmtNumber(summary?.tagit) },
      { label: t('admin.tiri.modules.rateit'), value: fmtNumber(summary?.rateit) }
    ],
    [summary, t]
  );

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>{t('admin.tiri.title')}</Text>
        </Row>
      </Header>

      <Col ref={setScrollEl} start noFlex marg="54px 0 0" wid="100%" ht="100vh" scrollY>
        <Row start gap="30px" pad="12px 24px" noFlex>
          <Dropdown
            admin
            title={t('admin.tiri.filters.labelStatus')}
            wid="180px"
            selected={selected.status.value}
            options={statusOptions}
            handleSelect={(o) => setStatusFilter(o.opt)}
            rounded
            small
          />

          <Dropdown
            admin
            title={t('admin.tiri.filters.module')}
            wid="150px"
            selected={selected.module.value}
            options={moduleOptions}
            handleSelect={(o) => setModuleFilter(o.opt)}
            rounded
            small
            // Filtering by label status already pins the module to Tag It, so leaving this
            // enabled would offer a combination the list can't honour.
            disabled={!!statusFilter}
          />

          <Dropdown
            admin
            title={t('admin.tiri.filters.source')}
            wid="190px"
            selected={selected.source.value}
            options={sourceOptions}
            handleSelect={(o) => setSourceFilter(o.opt)}
            rounded
            small
          />

          <Col noFlex>
            <Text marg="0 16px 4px" b2 med>
              {t('admin.tiri.filters.team')}
            </Text>
            <Row noFlex wid="300px">
              <SearchUsers
                userType="team"
                selected={teamFilter}
                handleSelect={setTeamFilter}
                includeDummy={true}
                noRounded
              />
              {teamFilter && (
                <Row start between wid="100%" center>
                  <Row noFlex center>
                    <Avatar small user={teamFilter} />
                    <Col marg="0 0 0 8px" noFlex ht="40px">
                      <Text>{teamFilter.name}</Text>
                    </Col>
                  </Row>
                  <Clickable onClick={() => setTeamFilter(null)}>
                    <Icon type="close" dimensions={14} />
                  </Clickable>
                </Row>
              )}
            </Row>
          </Col>
        </Row>

        <Col noFlex wid="100%" pad="0 24px">
          <ParticipationSummary loading={summaryLoading} items={summaryItems} />
        </Col>

        <List
          data={items}
          loading={loading}
          dataId={dataId}
          onClick={handleRowClick}
          scrollEl={scrollEl}
        />

        {paging && (
          <Col wid="100%" ht="140px" marg="8px 0 24px" centerAll noFlex>
            <Spinner2 spinnerSize="44px" />
          </Col>
        )}

        <div ref={observerTarget} />
      </Col>

      <TiriPanel dataId={dataId} closeModal={closePanel} />
    </Col>
  );
};

export default Tiri;
